import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut,
  FileText,
  Download,
  Music,
  Folder,
  File,
  Search,
  Eye,
  Video,
  X,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import { PDFViewer } from "../components/PDFViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

const COURSE_CATEGORIES = {
  "Instruments": [
    "Drums",
    "Bass",
    "Piano",
    "Guitar",
    "Trumpet",
    "Trombone",
    "Clarinet",
    "Flute",
    "Oboe",
    "Alto Sax",
    "Tenor Sax",
    "Soprano Sax",
    "Baritone Sax",
    "Vocal",
  ],
  "Theory & Composition": [
    "Music Theory",
    "Jazz Theory",
    "Arrangement",
    "Improvisation",
  ],
  "Production": [
    "Studio Production",
    "AI Creative Music",
  ],
};

// Flat list with "All" option for filtering
const ALL_COURSES = ["All", ...Object.values(COURSE_CATEGORIES).flat()];

export const CourseMaterials = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewingPdf, setViewingPdf] = useState(null);
  const [viewingVideo, setViewingVideo] = useState(null);
  const [materialTypeFilter, setMaterialTypeFilter] = useState("all");
  const userType = localStorage.getItem("mms_user_type");

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("mms_token")}` },
  });

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`, getAuthHeaders());
      setMaterials(response.data);
    } catch (error) {
      console.error("Error fetching materials:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleDownload = async (material) => {
    try {
      // First try to get signed URL
      const urlResponse = await axios.get(
        `${API}/materials/url/${material.id}`,
        getAuthHeaders()
      );
      
      if (urlResponse.data.signed_url) {
        // Open signed URL in new tab for download
        window.open(urlResponse.data.signed_url, "_blank");
        toast.success("Download started");
      } else if (urlResponse.data.download_url) {
        // Fallback to direct download
        const response = await axios.get(
          `${API}/materials/download/${material.id}`,
          {
            ...getAuthHeaders(),
            responseType: "blob",
          }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", material.file_name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Download started");
      }
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const handleView = async (material) => {
    const isPdf = material.file_name.toLowerCase().endsWith('.pdf') || material.material_type === 'pdf';
    const isVideo = material.material_type === 'video' || material.content_type?.startsWith('video/');
    
    if (isPdf) {
      setViewingPdf(material);
    } else if (isVideo) {
      // Get signed URL for video
      try {
        const urlResponse = await axios.get(
          `${API}/materials/url/${material.id}`,
          getAuthHeaders()
        );
        setViewingVideo({
          ...material,
          signed_url: urlResponse.data.signed_url
        });
      } catch (error) {
        toast.error("Failed to load video");
      }
    } else {
      handleDownload(material);
    }
  };

  const filteredMaterials = materials.filter((m) => {
    const matchesCourse = selectedCourse === "All" || m.course === selectedCourse;
    const matchesSearch =
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = materialTypeFilter === "all" || 
      (materialTypeFilter === "pdf" && (!m.material_type || m.material_type === "pdf")) ||
      (materialTypeFilter === "video" && m.material_type === "video");
    return matchesCourse && matchesSearch && matchesType;
  });

  const groupedMaterials = filteredMaterials.reduce((acc, material) => {
    if (!acc[material.course]) {
      acc[material.course] = [];
    }
    acc[material.course].push(material);
    return acc;
  }, {});

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
        <div className={`w-8 h-8 border-2 rounded-full animate-spin ${theme === 'dark' ? 'border-white/20 border-t-white' : 'border-indigo-200 border-t-indigo-600'}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${theme === 'dark' ? 'bg-zinc-950/80 border-white/10' : 'bg-white/80 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="MMS Logo" className="h-12 w-auto object-contain logo-glass" />
            <span className={`text-lg font-semibold hidden sm:block ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>MMS Portal</span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-4">
            {userType === "instructor" ? (
              <>
                <Link to="/dashboard" className="glass-button-ghost text-sm">
                  Dashboard
                </Link>
                <Link to="/profile" className="glass-button-ghost text-sm">
                  Profile
                </Link>
              </>
            ) : (
              <Link to="/admin" className="glass-button-ghost text-sm">
                Admin Dashboard
              </Link>
            )}
            <Link to="/materials" className={`glass-button-ghost text-sm ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`}>
              Materials
            </Link>
            <ThemeToggle />
            <button onClick={handleLogout} className="glass-button-ghost text-sm flex items-center gap-1">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Course Materials</h1>
          <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>Access teaching resources and materials</p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input
                data-testid="materials-search"
                type="text"
                placeholder="Search materials..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="glass-input w-full pl-12"
              />
            </div>

            {/* Material Type Filter */}
            <div className={`flex rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
              <button
                data-testid="filter-all-types"
                onClick={() => setMaterialTypeFilter("all")}
                className={`px-4 py-2 text-sm transition-all ${
                  materialTypeFilter === "all"
                    ? theme === 'dark' ? 'bg-white text-black' : 'bg-indigo-600 text-white'
                    : theme === 'dark' ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                All
              </button>
              <button
                data-testid="filter-pdf-type"
                onClick={() => setMaterialTypeFilter("pdf")}
                className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${
                  materialTypeFilter === "pdf"
                    ? theme === 'dark' ? 'bg-white text-black' : 'bg-indigo-600 text-white'
                    : theme === 'dark' ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                PDFs
              </button>
              <button
                data-testid="filter-video-type"
                onClick={() => setMaterialTypeFilter("video")}
                className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${
                  materialTypeFilter === "video"
                    ? theme === 'dark' ? 'bg-white text-black' : 'bg-indigo-600 text-white'
                    : theme === 'dark' ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Video className="w-4 h-4" />
                Videos
              </button>
            </div>
          </div>

          {/* Course Filter */}
          <div className="mt-6 space-y-4">
            {/* All button */}
            <button
              data-testid="filter-all"
              onClick={() => setSelectedCourse("All")}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedCourse === "All"
                  ? theme === 'dark'
                    ? "bg-white text-black"
                    : "bg-indigo-600 text-white"
                  : theme === 'dark'
                    ? "bg-white/10 text-white hover:bg-white/20"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All
            </button>
            
            {/* Categorized filters */}
            {Object.entries(COURSE_CATEGORIES).map(([category, courses]) => (
              <div key={category}>
                <h4 className={`text-xs font-semibold mb-2 flex items-center gap-1.5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    category === 'Instruments' ? 'bg-blue-500' :
                    category === 'Theory & Composition' ? 'bg-purple-500' : 'bg-emerald-500'
                  }`} />
                  {category}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {courses.map((course) => (
                    <button
                      key={course}
                      data-testid={`filter-${course.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setSelectedCourse(course)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedCourse === course
                          ? theme === 'dark'
                            ? "bg-white text-black"
                            : "bg-indigo-600 text-white"
                          : theme === 'dark'
                            ? "bg-white/10 text-white hover:bg-white/20"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {course}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Materials Grid */}
        {Object.keys(groupedMaterials).length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-card p-16 text-center"
          >
            <Folder className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>No Materials Found</h3>
            <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
              {searchQuery || selectedCourse !== "All" || materialTypeFilter !== "all"
                ? "Try adjusting your filters"
                : "Materials will appear here when uploaded by admin"}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedMaterials).map(([course, courseMaterials], index) => (
              <motion.div
                key={course}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                  <Music className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                  {course}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {courseMaterials.map((material) => {
                    const isVideo = material.material_type === 'video';
                    const MaterialIcon = isVideo ? Video : FileText;
                    const iconColor = isVideo ? 'text-purple-500' : 'text-red-500';
                    const bgColor = isVideo 
                      ? (theme === 'dark' ? 'bg-purple-500/20' : 'bg-purple-50')
                      : (theme === 'dark' ? 'bg-red-500/20' : 'bg-red-50');
                    
                    return (
                      <div
                        key={material.id}
                        data-testid={`material-${material.id}`}
                        className="glass-card-hover p-6 group"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-xl ${bgColor}`}>
                            <MaterialIcon className={`w-6 h-6 ${iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{material.title}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                isVideo 
                                  ? 'bg-purple-500/20 text-purple-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {isVideo ? 'VIDEO' : 'PDF'}
                              </span>
                            </div>
                            <p className={`text-sm line-clamp-2 mt-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                              {material.description}
                            </p>
                            <div className={`flex items-center gap-2 mt-2 text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                              <span>{material.file_name}</span>
                              <span>•</span>
                              <span>{formatFileSize(material.file_size)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <button
                            data-testid={`view-${material.id}`}
                            onClick={() => handleView(material)}
                            className={`flex-1 glass-button-primary py-2 flex items-center justify-center gap-2 text-sm ${
                              theme === 'light' ? 'bg-indigo-600 hover:bg-indigo-700' : ''
                            }`}
                          >
                            <Eye className="w-4 h-4" />
                            {isVideo ? 'Play' : 'View'}
                          </button>
                          <button
                            data-testid={`download-${material.id}`}
                            onClick={() => handleDownload(material)}
                            className="flex-1 glass-button-secondary py-2 flex items-center justify-center gap-2 text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* PDF Viewer Modal */}
      {viewingPdf && (
        <PDFViewer
          pdfUrl={`${API}/materials/download/${viewingPdf.id}`}
          fileName={viewingPdf.file_name}
          materialId={viewingPdf.id}
          onClose={() => setViewingPdf(null)}
          onDownload={() => handleDownload(viewingPdf)}
        />
      )}

      {/* Video Player Modal */}
      {viewingVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative max-w-4xl w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium truncate pr-4">{viewingVideo.title}</h3>
              <button
                onClick={() => setViewingVideo(null)}
                className="p-2 rounded-lg hover:bg-white/10 text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <video
              src={viewingVideo.signed_url}
              controls
              autoPlay
              className="w-full rounded-xl"
              style={{ maxHeight: '70vh' }}
            >
              Your browser does not support the video tag.
            </video>
            <p className="text-xs text-slate-500 text-center mt-2">
              URL expires in 15 minutes
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
